/**
 * TicketsAdmin — owner/admin review of inbound contact-form tickets
 * (Settings → Admin → Tickets).
 *
 * The public contact form (backend contact.py) now persists each submission to
 * db.support_tickets with status "open"; this is the in-app way to SEE and triage
 * them. List + mark-resolved (GET/PATCH /api/v1/support-tickets, admin-gated).
 * Exact mirror of WaitlistAdmin.tsx; the mark-resolved action is the only addition.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, LifeBuoy, CheckCircle2, ArrowRightLeft, Paperclip, Sparkles, ChevronRight, MessageSquare } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import { useViewAs } from '../../hooks/useViewAs';
import { api } from '../../utils/api';
import TicketTriageCard, { AiTriage } from './TicketTriageCard';
import { firstResponseSla } from '../../utils/ticketSla';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

interface TicketAttachment {
  file_id: string;
  filename: string;
  content_type?: string;
  size_bytes?: number;
  uploaded_at?: string;
}

interface SupportTicket {
  id: string;
  name?: string;
  email?: string;
  subject?: string;
  message: string;
  status?: string;
  source?: string;
  created_at?: string;
  reporter?: { user_id?: string; email?: string; firm_id?: string; role?: string };
  context?: { route?: string; case_id?: string; app_version?: string; user_agent?: string };
  attachments?: TicketAttachment[];
  ai_triage?: AiTriage;
  messages?: TicketMessage[];
}

interface TicketMessage {
  id: string;
  author?: { user_id?: string; email?: string; role?: string };
  author_is_reporter?: boolean;
  body: string;
  internal?: boolean;
  created_at?: string;
}

const fmtDate = (d?: string) =>
  d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// Forensic completeness (the full case UUID) belongs on hover, not inline.
const shortCase = (id: string) => (id.length > 10 ? `${id.slice(0, 8)}…` : id);

const fmtErrNoteDate = (d?: string) => {
  if (!d) return '';
  const t = new Date(d);
  return isNaN(t.getTime()) ? d : t.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function StatusBadge({ status }: { status?: string }) {
  const s = (status || 'open').toLowerCase();
  const resolved = s === 'resolved';
  // Salience maps to actionability (K3): OPEN is the work queue → win the column
  // (full-strength text + brighter outline); RESOLVED is terminal, no action → recede
  // (dim text + hairline border). Hueless throughout — no status hue, never brand orange
  // (orange is reserved for nav + primary action; the active tab underline carries it).
  return (
    <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-mono uppercase tracking-wide"
      style={resolved
        ? { color: 'var(--color-text-dim)', borderColor: 'var(--color-border)' }
        : { color: 'var(--color-text-primary)', borderColor: 'var(--color-text-muted)' }}>
      {resolved && <CheckCircle2 className="w-3 h-3" />}
      {s}
    </span>
  );
}

export default function TicketsAdmin() {
  const { getToken } = useAuth();
  // Slice 1: "View as {reporter} here" — reuses the I2 read-only impersonation
  // flow, landing on the exact route the ticket was filed from.
  const { start: startViewAs, busy: viewAsBusy, error: viewAsError } = useViewAs();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [postingNote, setPostingNote] = useState(false);
  const [noteInternal, setNoteInternal] = useState(true); // default internal — explicit choice to email the reporter
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'resolved'>('all');
  const [sourceFilter, setSourceFilter] = useState<'all' | 'in_app' | 'contact_form'>('all');

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const data = await api.get<{ total: number; tickets: SupportTicket[] }>('/api/v1/support-tickets', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (active) setTickets(data?.tickets || []);
      } catch (e: any) {
        if (active) setError(e?.message || 'Could not load support tickets.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [getToken]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return tickets.filter((t) => {
      const st = (t.status || 'open') === 'resolved' ? 'resolved' : 'open';
      if (statusFilter !== 'all' && st !== statusFilter) return false;
      if (sourceFilter !== 'all') {
        const src = t.source === 'in_app' ? 'in_app' : 'contact_form';
        if (src !== sourceFilter) return false;
      }
      // reporter.email is included so searching an in-app ticket by its reporter
      // works (in-app tickets carry no top-level name/email).
      if (needle && !`${t.name || ''} ${t.email || ''} ${t.subject || ''} ${t.message || ''} ${t.reporter?.email || ''}`.toLowerCase().includes(needle))
        return false;
      return true;
    });
  }, [tickets, q, statusFilter, sourceFilter]);

  const openCount = useMemo(() => tickets.filter((t) => (t.status || 'open') !== 'resolved').length, [tickets]);

  const markResolved = async (id: string) => {
    try {
      const token = await getToken();
      await api.patch(
        `/api/v1/support-tickets/${id}`,
        { status: 'resolved' },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, status: 'resolved' } : t)));
    } catch (e: any) {
      setError(e?.message || 'Could not update the ticket.');
    }
  };

  // Attachments are admin-gated binaries; a plain link can't carry the Bearer
  // token, so fetch the bytes with auth and open the blob in a new tab.
  const viewAttachment = async (ticketId: string, att: TicketAttachment) => {
    try {
      const token = await getToken();
      const resp = await fetch(
        `${BACKEND_URL}/api/v1/support-tickets/${ticketId}/attachments/${att.file_id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error('Could not load the attachment.');
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      setError(e?.message || 'Could not open the attachment.');
    }
  };

  const postNote = async (ticketId: string) => {
    const text = noteDraft.trim();
    if (!text || postingNote) return;
    setPostingNote(true);
    try {
      const token = await getToken();
      const res: any = await api.post(
        `/api/v1/support-tickets/${ticketId}/messages`,
        { body: text, internal: noteInternal },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const msg: TicketMessage = { id: res.id, author: res.author, body: res.body, internal: res.internal, created_at: res.created_at };
      setTickets((prev) => prev.map((t) => (t.id === ticketId ? { ...t, messages: [...(t.messages || []), msg] } : t)));
      setNoteDraft('');
    } catch (e: any) {
      setError(e?.message || 'Could not post the note.');
    } finally {
      setPostingNote(false);
    }
  };

  const viewAsReporter = async (t: SupportTicket) => {
    const uid = t.reporter?.user_id;
    const route = t.context?.route;
    if (!uid || !route || viewAsBusy) return;
    const who = t.reporter?.email || uid;
    const ok = window.confirm(
      `View as ${who} on ${route}?\n\n` +
        `This opens a READ-ONLY impersonation session on the exact page they ` +
        `reported from — you'll see what they see, but no changes can be made ` +
        `until you exit view-as.`,
    );
    if (!ok) return;
    try {
      await startViewAs(uid, route);
    } catch {
      /* surfaced via viewAsError below */
    }
  };

  return (
    <div className="space-y-6">
      {/* Plain section header — no orange fill (K3 gate: orange is reserved for nav +
          primary action; the active tab underline already carries the accent here). */}
      <div className="flex items-center gap-3">
        <LifeBuoy className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        <div>
          <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>Support Tickets</h2>
          <p className="cs-meta" style={{ color: 'var(--color-text-muted)' }}>
            Inbound messages — public contact form + in-app reports
          </p>
        </div>
      </div>

      <div
        className="rounded-xl p-6"
        style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}
      >
        {viewAsError && (
          <div
            className="rounded-lg px-4 py-2 text-sm mb-4"
            style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-status-adverse)', color: 'var(--color-status-adverse)' }}
          >
            {viewAsError}
          </div>
        )}
        {/* Summary + search */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-6 text-sm" style={{ color: 'var(--color-text-muted)' }}>
            <span><strong style={{ color: 'var(--color-text-primary)' }}>{tickets.length}</strong> total</span>
            <span><strong style={{ color: 'var(--color-text-primary)' }}>{openCount}</strong> open</span>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--color-text-dim)' }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, subject…"
              className="pl-9 pr-3 py-2 rounded-lg text-sm w-72 max-w-full"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
            />
          </div>
        </div>

        {/* Triage filters — hueless segmented controls (active = text-primary on an
            elevated fill, inactive = dim), mirroring the note-visibility toggle.
            Client-side over the already-loaded list. */}
        {!loading && !error && tickets.length > 0 && (
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 text-xs font-mono uppercase tracking-wide">
            <div className="inline-flex rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
              {([['All', 'all'], ['Open', 'open'], ['Resolved', 'resolved']] as [string, typeof statusFilter][]).map(([label, val], i, arr) => (
                <button key={val} type="button" onClick={() => setStatusFilter(val)}
                  className={`px-2.5 py-1 transition-colors ${i === 0 ? 'rounded-l-lg' : ''} ${i === arr.length - 1 ? 'rounded-r-lg' : ''}`}
                  style={statusFilter === val ? { background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' } : { color: 'var(--color-text-dim)' }}>
                  {label}
                </button>
              ))}
            </div>
            <div className="inline-flex rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
              {([['All sources', 'all'], ['In-app', 'in_app'], ['Contact form', 'contact_form']] as [string, typeof sourceFilter][]).map(([label, val], i, arr) => (
                <button key={val} type="button" onClick={() => setSourceFilter(val)}
                  className={`px-2.5 py-1 transition-colors ${i === 0 ? 'rounded-l-lg' : ''} ${i === arr.length - 1 ? 'rounded-r-lg' : ''}`}
                  style={sourceFilter === val ? { background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' } : { color: 'var(--color-text-dim)' }}>
                  {label}
                </button>
              ))}
            </div>
            {(statusFilter !== 'all' || sourceFilter !== 'all') && (
              <span className="normal-case" style={{ color: 'var(--color-text-muted)' }}>{filtered.length} shown</span>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--color-text-muted)' }} />
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm" style={{ color: 'var(--color-status-adverse)' }}>{error}</p>
        ) : filtered.length === 0 ? (
          <p className="py-12 text-center text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {tickets.length === 0 ? 'No tickets yet.' : 'No tickets match your search.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ color: 'var(--color-text-dim)' }}>
                  <th className="font-mono text-xs uppercase tracking-wide py-2 pr-4">From</th>
                  <th className="font-mono text-xs uppercase tracking-wide py-2 pr-4">Subject</th>
                  <th className="font-mono text-xs uppercase tracking-wide py-2 pr-4">Message</th>
                  <th className="font-mono text-xs uppercase tracking-wide py-2 pr-4">Reporter</th>
                  <th className="font-mono text-xs uppercase tracking-wide py-2 pr-4">Status</th>
                  <th className="font-mono text-xs uppercase tracking-wide py-2 pr-4 text-right whitespace-nowrap">Date</th>
                  <th className="font-mono text-xs uppercase tracking-wide py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => {
                  const isResolved = (t.status || 'open') === 'resolved';
                  const canViewAs = t.source === 'in_app' && !!t.reporter?.user_id && !!t.context?.route;
                  const expanded = expandedId === t.id;
                  // "From" merges the old Name + Email columns: contact-form tickets
                  // carry a distinct name + email; in-app reports fall back to the
                  // reporter email for both, which duplicated the same string across
                  // two columns. Lead with the name (or email), show the email beneath
                  // only when it adds something the primary line didn't.
                  const fromPrimary = t.name || t.reporter?.email || t.email || '—';
                  const fromEmail = t.email || t.reporter?.email;
                  return (
                    <React.Fragment key={t.id}>
                    <tr className="border-t" style={{ borderColor: 'var(--color-border)' }}>
                      <td className="py-3 pr-4 align-top">
                        <div style={{ color: 'var(--color-text-primary)' }}>{fromPrimary}</div>
                        {fromEmail && fromEmail !== fromPrimary && (
                          <div className="text-xs mt-0.5" style={{ color: 'var(--color-text-dim)' }}>{fromEmail}</div>
                        )}
                      </td>
                      <td className="py-3 pr-4 align-top" style={{ color: 'var(--color-text-secondary)' }}>{t.subject || '—'}</td>
                      <td className="py-3 pr-4 max-w-[260px] align-top" style={{ color: 'var(--color-text-secondary)' }}>
                        <div className="truncate" title={t.message}>{t.message || '—'}</div>
                        {t.attachments && t.attachments.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {t.attachments.map((a) => (
                              <button
                                key={a.file_id}
                                type="button"
                                onClick={() => viewAttachment(t.id, a)}
                                title={`Open ${a.filename}`}
                                className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] max-w-[160px] transition-colors hover:text-[var(--color-brand)] hover:border-[var(--color-brand)]"
                                style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}
                              >
                                <Paperclip className="w-3 h-3 shrink-0" />
                                <span className="truncate">{a.filename}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        {t.ai_triage && (
                          <button
                            type="button"
                            onClick={() => setExpandedId(expanded ? null : t.id)}
                            aria-expanded={expanded}
                            className="mt-1 inline-flex items-center gap-1 text-[11px] transition-colors hover:text-[var(--color-text-secondary)]"
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            <Sparkles className="w-3 h-3" />
                            <span>
                              AI triage
                              {t.ai_triage.status === 'queued' && ' analyzing…'}
                              {t.ai_triage.status === 'done' && t.ai_triage.severity && (
                                <>
                                  {' · '}
                                  <span style={t.ai_triage.severity === 'high' ? { color: 'var(--color-status-adverse)' } : undefined}>
                                    {t.ai_triage.severity}
                                  </span>
                                </>
                              )}
                            </span>
                            <ChevronRight className="w-3 h-3 transition-transform" style={{ transform: expanded ? 'rotate(90deg)' : 'none' }} />
                          </button>
                        )}
                        {!t.ai_triage && (
                          <button
                            type="button"
                            onClick={() => setExpandedId(expanded ? null : t.id)}
                            aria-expanded={expanded}
                            className="mt-1 inline-flex items-center gap-1 text-[11px] transition-colors hover:text-[var(--color-text-secondary)]"
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            <MessageSquare className="w-3 h-3" />
                            Internal notes{t.messages && t.messages.length > 0 ? ` · ${t.messages.length}` : ''}
                            <ChevronRight className="w-3 h-3 transition-transform" style={{ transform: expanded ? 'rotate(90deg)' : 'none' }} />
                          </button>
                        )}
                      </td>
                      <td className="py-3 pr-4 align-top">
                        {t.source === 'in_app' ? (
                          <div className="space-y-0.5 font-mono text-xs" style={{ color: 'var(--color-text-muted)' }}>
                            {/* Lead with role · firm (email already shown in the From column). */}
                            {t.reporter?.role || t.reporter?.firm_id ? (
                              <div>
                                {t.reporter?.role || ''}
                                {t.reporter?.role && t.reporter?.firm_id ? ' · ' : ''}
                                {t.reporter?.firm_id || ''}
                              </div>
                            ) : (
                              <div>{t.reporter?.user_id || '—'}</div>
                            )}
                            {(t.context?.route || t.context?.case_id) && (
                              <div title={t.context?.case_id ? `case ${t.context.case_id}` : undefined}>
                                {t.context?.route ? `on ${t.context.route}` : ''}
                                {t.context?.route && t.context?.case_id ? ' · ' : ''}
                                {t.context?.case_id ? `case ${shortCase(t.context.case_id)}` : ''}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--color-text-dim)' }}>—</span>
                        )}
                      </td>
                      <td className="py-3 pr-4 align-top">
                        <StatusBadge status={t.status} />
                        {t.source === 'in_app' && (() => {
                          const sla = firstResponseSla({ createdAt: t.created_at, severity: t.ai_triage?.severity, status: t.status, messages: t.messages });
                          if (!sla || sla.state === 'met') return null;
                          const color = sla.state === 'breached'
                            ? 'var(--color-status-adverse)'
                            : sla.state === 'at_risk'
                              ? 'var(--color-needs-human-text)'
                              : 'var(--color-text-dim)';
                          return <div className="mt-1 font-mono text-[10px] whitespace-nowrap" style={{ color }}>{sla.label}</div>;
                        })()}
                      </td>
                      <td className="py-3 pr-4 text-right align-top font-mono text-xs whitespace-nowrap" style={{ color: 'var(--color-text-muted)' }}>{fmtDate(t.created_at)}</td>
                      <td className="py-3 text-right align-top">
                        {canViewAs || !isResolved ? (
                          <div className="flex flex-col items-end gap-1.5">
                            {canViewAs && (
                              <button
                                type="button"
                                onClick={() => viewAsReporter(t)}
                                disabled={viewAsBusy}
                                title={`View as ${t.reporter?.email || t.reporter?.user_id} on ${t.context?.route}`}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-mono uppercase tracking-wide whitespace-nowrap border transition-colors cursor-pointer hover:text-[var(--color-brand)] hover:border-[var(--color-brand)] disabled:cursor-not-allowed disabled:opacity-50"
                                style={{ color: 'var(--color-text-secondary)', borderColor: 'var(--color-border)' }}
                              >
                                <ArrowRightLeft className="w-3 h-3" />
                                View as here
                              </button>
                            )}
                            {!isResolved && (
                              <button
                                type="button"
                                onClick={() => markResolved(t.id)}
                                className="inline-flex items-center gap-1 whitespace-nowrap rounded-lg border px-2 py-1 text-xs transition-opacity hover:opacity-80"
                                style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)', background: 'transparent' }}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Mark resolved
                              </button>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: 'var(--color-text-dim)' }}>—</span>
                        )}
                      </td>
                    </tr>
                    {expanded && (
                      <tr>
                        <td colSpan={7} className="p-0" style={{ background: 'var(--color-bg-elevated)' }}>
                          {/* Pin the detail to the left edge + bound its width: the table
                              scrolls horizontally, so an un-pinned colSpan panel would push
                              the compose button off-screen to the right (K3). */}
                          <div className="sticky left-0 px-4 pb-4 pt-3" style={{ maxWidth: t.ai_triage ? '68rem' : '48rem' }}>
                          {/* Triage + notes sit side-by-side when both exist, so the expanded
                              row fills its width instead of leaving a right-hand void (K3).
                              Stacks under lg; stays sticky-left so the compose box is reachable
                              when the dense table scrolls horizontally on a narrow laptop. */}
                          <div className={t.ai_triage ? 'grid gap-3 lg:grid-cols-2 items-start' : ''}>
                          {t.ai_triage && (
                            <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                              <TicketTriageCard triage={t.ai_triage} />
                            </div>
                          )}
                          {/* Internal notes thread (admin-only, v1) */}
                          <div className="rounded-lg p-4" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)' }}>
                            <div className="flex items-center gap-2 mb-2">
                              <MessageSquare className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                              <span className="font-mono text-[10px] uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                                Internal notes
                              </span>
                            </div>
                            {t.messages && t.messages.length > 0 ? (
                              <ul className="mb-3">
                                {t.messages.map((m, mi) => (
                                  <li
                                    key={m.id}
                                    className="text-sm py-2 first:pt-0"
                                    style={{ borderTop: mi === 0 ? 'none' : '1px solid var(--color-border)' }}
                                  >
                                    <div style={{ color: 'var(--color-text-secondary)' }}>{m.body}</div>
                                    <div className="font-mono text-[10px] mt-1.5" style={{ color: 'var(--color-text-dim)' }}>
                                      {m.author_is_reporter ? 'Reporter' : (m.author?.email || m.author?.role || 'admin')}
                                      {!m.author_is_reporter ? (m.internal ? ' · internal' : ' · reply') : ''}
                                      {m.created_at ? ` · ${fmtErrNoteDate(m.created_at)}` : ''}
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-xs mb-3" style={{ color: 'var(--color-text-dim)' }}>
                                No notes yet. Internal notes are visible only to platform admins.
                              </p>
                            )}
                            <div>
                              {/* An in-app reporter has an account, so an admin can either
                                  keep a note internal or send a reporter-visible reply
                                  (which emails them). Contact-form tickets have no account,
                                  so only internal notes apply. */}
                              {canViewAs && (
                                <div className="inline-flex rounded-lg border mb-2 text-xs font-mono uppercase tracking-wide" style={{ borderColor: 'var(--color-border)' }}>
                                  {([['Internal note', true], ['Reply to reporter', false]] as [string, boolean][]).map(([label, val], i) => {
                                    const active = noteInternal === val;
                                    return (
                                      <button
                                        key={label}
                                        type="button"
                                        onClick={() => setNoteInternal(val)}
                                        className={`px-2.5 py-1 transition-colors ${i === 0 ? 'rounded-l-lg' : 'rounded-r-lg'}`}
                                        style={active
                                          ? { background: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }
                                          : { color: 'var(--color-text-dim)' }}
                                      >
                                        {label}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                              <textarea
                                value={expanded ? noteDraft : ''}
                                onChange={(e) => setNoteDraft(e.target.value)}
                                rows={2}
                                placeholder={noteInternal || !canViewAs ? 'Add an internal note…' : 'Reply to the reporter (emails them)…'}
                                className="w-full rounded px-3 py-2 text-sm outline-none focus:outline-none resize-none"
                                style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }}
                              />
                              <div className="flex justify-end mt-2">
                                <button
                                  type="button"
                                  onClick={() => postNote(t.id)}
                                  disabled={!noteDraft.trim() || postingNote}
                                  className="inline-flex items-center gap-1 rounded px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed"
                                  style={
                                    noteDraft.trim() && !postingNote
                                      ? { background: 'var(--color-brand)', color: 'var(--color-on-accent)' }
                                      : { background: 'transparent', border: '1px solid var(--color-text-muted)', color: 'var(--color-text-muted)' }
                                  }
                                >
                                  {postingNote ? 'Sending…' : noteInternal || !canViewAs ? 'Add note' : 'Send reply'}
                                </button>
                              </div>
                            </div>
                          </div>
                          </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
